import type { CreateOrderDto, CreateOrderResponseDto, CreateOrderWithItemsDto, GetOrderDto, GetSommelierClientOrdersDto, OrderDto, UpdateOrderDto, UpdateOrderWithItemsDto } from './models';
import { RestService, Rest } from '@abp/ng.core';
import type { PagedResultDto } from '@abp/ng.core';
import { Injectable } from '@angular/core';
import type { EnumDto } from '../enums/models';
import type { OrderStatus } from '../enums/order-status.enum';
import type { SommelierDto } from '../sommeliers/models';

@Injectable({
  providedIn: 'root',
})
export class OrderService {
  apiName = 'Default';
  

  cancelOrder = (orderId: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, OrderDto>({
      method: 'POST',
      url: `/api/app/order/cancel-order/${orderId}`,
    },
    { apiName: this.apiName,...config });
  

  changeOrderStatus = (orderId: number, newStatus: OrderStatus, config?: Partial<Rest.Config>) =>
    this.restService.request<any, OrderDto>({
      method: 'POST',
      url: `/api/app/order/change-order-status/${orderId}`,
      params: { newStatus },
    },
    { apiName: this.apiName,...config });
  

  confirmBankPayment = (orderId: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, OrderDto>({
      method: 'POST',
      url: `/api/app/order/confirm-bank-payment/${orderId}`,
    },
    { apiName: this.apiName,...config });
  

  create = (input: CreateOrderDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, OrderDto>({
      method: 'POST',
      url: '/api/app/order',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  createOrderByBackoffice = (input: CreateOrderWithItemsDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, CreateOrderResponseDto>({
      method: 'POST',
      url: '/api/app/order/order-by-backoffice',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  createOrderByClient = (input: CreateOrderWithItemsDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, CreateOrderResponseDto>({
      method: 'POST',
      url: '/api/app/order/order-by-client',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  createWithItems = (input: CreateOrderWithItemsDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, CreateOrderResponseDto>({
      method: 'POST',
      url: '/api/app/order/with-items',
      body: input,
    },
    { apiName: this.apiName,...config });
  

  delete = (id: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, void>({
      method: 'DELETE',
      url: `/api/app/order/${id}`,
    },
    { apiName: this.apiName,...config });
  

  get = (id: number, config?: Partial<Rest.Config>) =>
    this.restService.request<any, OrderDto>({
      method: 'GET',
      url: `/api/app/order/${id}`,
    },
    { apiName: this.apiName,...config });
  

  getClientOrders = (input: GetSommelierClientOrdersDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<OrderDto>>({
      method: 'GET',
      url: '/api/app/order/client-orders',
      params: { clientId: input.clientId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getList = (input: GetOrderDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<OrderDto>>({
      method: 'GET',
      url: '/api/app/order',
      params: { keyword: input.keyword, orderStatus: input.orderStatus, fromCreationTime: input.fromCreationTime, toCreationTime: input.toCreationTime, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getOrderStatuses = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, EnumDto[]>({
      method: 'GET',
      url: '/api/app/order/order-statuses',
    },
    { apiName: this.apiName,...config });
  

  getOrdersByCurrentUser = (input: GetOrderDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<OrderDto>>({
      method: 'GET',
      url: '/api/app/order/orders-by-current-user',
      params: { keyword: input.keyword, orderStatus: input.orderStatus, fromCreationTime: input.fromCreationTime, toCreationTime: input.toCreationTime, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  getSommelierBySlugBySlug = (slug: string, config?: Partial<Rest.Config>) =>
    this.restService.request<any, SommelierDto>({
      method: 'GET',
      url: '/api/app/order/sommelier-by-slug',
      params: { slug },
    },
    { apiName: this.apiName,...config });
  

  getSommelierOrders = (input: GetOrderDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, PagedResultDto<OrderDto>>({
      method: 'GET',
      url: '/api/app/order/sommelier-orders',
      params: { keyword: input.keyword, orderStatus: input.orderStatus, fromCreationTime: input.fromCreationTime, toCreationTime: input.toCreationTime, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
    },
    { apiName: this.apiName,...config });
  

  update = (id: number, input: UpdateOrderDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, OrderDto>({
      method: 'PUT',
      url: `/api/app/order/${id}`,
      body: input,
    },
    { apiName: this.apiName,...config });
  

  updateWithItems = (id: number, input: UpdateOrderWithItemsDto, config?: Partial<Rest.Config>) =>
    this.restService.request<any, OrderDto>({
      method: 'PUT',
      url: `/api/app/order/${id}/with-items`,
      body: input,
    },
    { apiName: this.apiName,...config });

  generateExcel = (config?: Partial<Rest.Config>) =>
    this.restService.request<any, Blob>({
      method: 'POST',
      responseType: 'blob',
      url: '/api/app/order/generate-excel',
    },
    { apiName: this.apiName,...config });

  constructor(private restService: RestService) {}
}
